import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { trackEvent } from "../lib/api";
import { getLeakCategories } from "../lib/api/index";
import { logger } from "../lib/logger";

// SystemForge AI — Landing
//
// Phase P1-fix dispatch — adds the CEO-approved commercial packaging
// (price disclosure, ascension ladder, founder/trust framing, reserved
// testimonial slots). The original two-section structure (hero + 8 leak
// categories) is preserved verbatim. Everything below is additive.
//
// HARD RULE per CEO directive: no fabricated testimonials, no fake names,
// no invented metrics, no "as seen in" badges. The three testimonial
// slots use the three approved placeholder strings only.

const ASCENSION_LEVELS = [
  {
    id: "level1",
    eyebrow: "Level 1",
    title: "Business Health Audit",
    price: "$97",
    summary: "The diagnostic, the scores, the executive summary, the PDF.",
  },
  {
    id: "level2",
    eyebrow: "Level 2",
    title: "Strategic Business Consultation",
    price: "$1,000",
    summary: "A founder-led working session that turns the audit into a 90-day operating plan.",
  },
  {
    id: "level3",
    eyebrow: "Level 3",
    title: "SystemForge Enterprise",
    price: "Starting at $10,000",
    summary: "Full implementation engagement — systems, automation, retention, and operations rebuilt to spec.",
  },
];

const AUDIT_INCLUDES = [
  "Business Health Score",
  "Revenue Recovery Score",
  "Opportunity Score",
  "Automation Readiness Score",
  "Operations Score",
  "Retention Score",
  "Executive Summary",
  "Downloadable PDF Report",
  "Recommended Next Steps",
];

export default function Landing() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    trackEvent("landing_view", "/");
    getLeakCategories()
      .then((data) => { if (!cancelled) setCategories(data.categories || []); })
      .catch((err) => {
        logger.error("leak_categories_load_failed", { err: err?.message });
        if (!cancelled) {
          setCategories([]);
          setLoadError("Could not load category data. Refresh to retry.");
        }
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="landing-page">
      {/* -------- A. Hero -------- */}
      <section className="sf-container" style={{ padding: "88px 24px 64px" }}>
        <div className="sf-eyebrow" data-testid="landing-eyebrow">SystemForge AI &middot; Audit Engine</div>
        <h1
          data-testid="landing-headline"
          className="sf-display"
          style={{ fontSize: "clamp(40px, 6vw, 72px)", marginTop: 18, marginBottom: 20, maxWidth: 920, lineHeight: 1.05 }}
        >
          Find the revenue leaks, system gaps,<br />
          and bottlenecks holding<br />
          your business <span className="sf-gold">back</span>.
        </h1>
        <p
          data-testid="landing-subtext"
          style={{ color: "var(--sf-muted)", fontSize: 18, maxWidth: 680, marginBottom: 36 }}
        >
          Answer 12 questions about how your business runs. We score 8 systems
          against benchmarks, calculate the monthly revenue you're losing to
          gaps, and tell you what to fix first. The free score is yours. The
          full 13-section playbook is $97.
        </p>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
          <Link to="/audit/snapshot" className="sf-btn" data-testid="landing-cta-primary">
            Start Your $97 Business Audit
          </Link>
          <a href="#audit-offer" className="sf-btn-secondary" data-testid="landing-cta-secondary">
            See what's included
          </a>
        </div>
      </section>

      {/* -------- B. $97 Audit Section -------- */}
      <section id="audit-offer" className="sf-container" style={{ padding: "24px 24px 64px" }}>
        <div className="sf-card" data-testid="landing-audit-offer">
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", flexWrap: "wrap", gap: 18, marginBottom: 24 }}>
            <div>
              <div className="sf-eyebrow" style={{ marginBottom: 8 }}>The audit</div>
              <h2 className="sf-display" style={{ fontSize: 32, margin: 0 }}>
                SystemForge Business Health Audit
              </h2>
            </div>
            <div data-testid="landing-audit-price" className="sf-mono sf-gold" style={{ fontSize: 36, fontWeight: 600 }}>
              $97
            </div>
          </div>
          <div className="sf-divider" style={{ marginBottom: 22 }} />
          <div className="sf-mono sf-muted-text" style={{ fontSize: 12, marginBottom: 14, letterSpacing: "0.1em", textTransform: "uppercase" }}>
            Includes
          </div>
          <ul data-testid="landing-audit-includes" style={{
            listStyle: "none", margin: 0, padding: 0,
            display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
            gap: 10,
          }}>
            {AUDIT_INCLUDES.map((item) => (
              <li
                key={item}
                data-testid={`landing-audit-include-${item.toLowerCase().replace(/\s+/g, "-")}`}
                style={{ color: "var(--sf-offwhite)", fontSize: 15, paddingLeft: 18, position: "relative" }}
              >
                <span className="sf-gold" style={{ position: "absolute", left: 0, top: 2 }}>—</span>
                {item}
              </li>
            ))}
          </ul>
          <div style={{ marginTop: 26 }}>
            <Link to="/audit/snapshot" className="sf-btn" data-testid="landing-audit-cta">
              Start the audit · $97
            </Link>
          </div>
        </div>
      </section>

      {/* -------- C. Ascension Ladder -------- */}
      <section className="sf-container" style={{ padding: "32px 24px 64px" }}>
        <div className="sf-eyebrow">The pathway</div>
        <h2 className="sf-display" style={{ fontSize: 32, marginTop: 12, marginBottom: 24 }}>
          Where the audit leads.
        </h2>
        <div data-testid="landing-ascension-ladder" style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 16,
        }}>
          {ASCENSION_LEVELS.map((lvl) => (
            <div key={lvl.id} className="sf-card" data-testid={`landing-${lvl.id}`}>
              <div className="sf-eyebrow" style={{ marginBottom: 8 }}>{lvl.eyebrow}</div>
              <div className="sf-display" style={{ fontSize: 22, marginBottom: 6, color: "var(--sf-offwhite)" }}>
                {lvl.title}
              </div>
              <div className="sf-mono sf-gold" style={{ fontSize: 18, marginBottom: 14, fontWeight: 600 }} data-testid={`landing-${lvl.id}-price`}>
                {lvl.price}
              </div>
              <div className="sf-divider" style={{ marginBottom: 14 }} />
              <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                {lvl.summary}
              </p>
            </div>
          ))}
        </div>
        <p data-testid="landing-ladder-note" className="sf-mono sf-muted-text" style={{ fontSize: 12, marginTop: 18, maxWidth: 720 }}>
          Levels 2 and 3 are introduced inside the paid audit report based on what your business actually needs.
        </p>
      </section>

      {/* -------- D. Founder / Credibility Block -------- */}
      <section className="sf-container" style={{ padding: "32px 24px 64px" }}>
        <div className="sf-card" data-testid="landing-founder-block">
          <div className="sf-eyebrow" style={{ marginBottom: 10 }}>Founder-led</div>
          <h2 className="sf-display" style={{ fontSize: 28, marginTop: 0, marginBottom: 16 }}>
            Built by an operator, not a consultancy.
          </h2>
          <p style={{ color: "var(--sf-offwhite)", fontSize: 15, lineHeight: 1.65, maxWidth: 720, marginBottom: 0 }}>
            SystemForge AI is a founder-led business systems review platform. The audit was designed
            from inside real revenue and operations teams — not from a slide deck. Every diagnostic
            question, every leak category, and every recommendation traces back to a problem an
            operator has actually faced.
          </p>
        </div>

        <div style={{ marginTop: 26 }}>
          <div className="sf-eyebrow" style={{ marginBottom: 14 }}>Why owners trust SystemForge</div>
          <div data-testid="landing-credibility-grid" style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
            gap: 16,
          }}>
            <div className="sf-card" data-testid="landing-credibility-1">
              <div className="sf-mono sf-muted-text" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
                Founder-led
              </div>
              <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                A founder-led business systems review. The audit was built by
                an operator who has run real revenue and operations teams.
              </p>
            </div>
            <div className="sf-card" data-testid="landing-credibility-2">
              <div className="sf-mono sf-muted-text" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
                Purpose
              </div>
              <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                Built to help owners find revenue leaks, system gaps, and the
                bottlenecks that quietly slow growth.
              </p>
            </div>
            <div className="sf-card" data-testid="landing-credibility-3">
              <div className="sf-mono sf-muted-text" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
                How it scores
              </div>
              <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                Deterministic scoring engine, AI-assisted explanation. The
                numbers come from your answers; the AI just puts them into
                plain English.
              </p>
            </div>
            <div className="sf-card" data-testid="landing-credibility-4">
              <div className="sf-mono sf-muted-text" style={{ fontSize: 11, letterSpacing: "0.12em", textTransform: "uppercase", marginBottom: 12 }}>
                Who it's for
              </div>
              <p style={{ color: "var(--sf-offwhite)", fontSize: 14, lineHeight: 1.55, margin: 0 }}>
                Designed for owners who need clarity before investing in
                bigger consulting or implementation work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* -------- E. Trust Framing -------- */}
      <section className="sf-container" style={{ padding: "32px 24px 64px" }}>
        <div className="sf-card" data-testid="landing-trust-block">
          <div className="sf-eyebrow" style={{ marginBottom: 10 }}>How the audit works</div>
          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 14, maxWidth: 760 }}>
            <li data-testid="landing-trust-purpose" style={{ color: "var(--sf-offwhite)", fontSize: 15, lineHeight: 1.6 }}>
              <span className="sf-gold sf-mono" style={{ marginRight: 10 }}>01</span>
              The audit helps you see what is leaking revenue, time, and opportunity in your business — and what to do about it.
            </li>
            <li data-testid="landing-trust-engine" style={{ color: "var(--sf-offwhite)", fontSize: 15, lineHeight: 1.6 }}>
              <span className="sf-gold sf-mono" style={{ marginRight: 10 }}>02</span>
              Every score is calculated by a deterministic engine. The numbers come from your answers and a fixed model — never from the AI.
            </li>
            <li data-testid="landing-trust-ai" style={{ color: "var(--sf-offwhite)", fontSize: 15, lineHeight: 1.6 }}>
              <span className="sf-gold sf-mono" style={{ marginRight: 10 }}>03</span>
              The AI only puts the engine's findings into plain English. It explains, not calculates.
            </li>
            <li data-testid="landing-trust-next" style={{ color: "var(--sf-offwhite)", fontSize: 15, lineHeight: 1.6 }}>
              <span className="sf-gold sf-mono" style={{ marginRight: 10 }}>04</span>
              You walk away with a free Business Health score, and the option to unlock the full 13-section playbook for $97.
            </li>
          </ul>
        </div>
      </section>

      {/* -------- F. The 8 leak categories (preserved from original) -------- */}
      <section id="categories" className="sf-container" style={{ padding: "32px 24px 88px" }}>
        <div className="sf-eyebrow">The 8 leak categories</div>
        <h2 className="sf-display" style={{ fontSize: 32, marginTop: 12, marginBottom: 12 }}>
          What we score you on.
        </h2>
        <p className="sf-muted-text" style={{ maxWidth: 720, marginBottom: 28, fontSize: 15, lineHeight: 1.6 }}>
          Eight systems every business runs on. The diagnostic measures how
          each one is performing and what it is costing you when it is not.
        </p>
        {(() => {
          if (loading) {
            return <div className="sf-muted-text" data-testid="landing-categories-loading">Loading categories…</div>;
          }
          if (loadError) {
            return <div className="sf-muted-text" data-testid="landing-categories-error">{loadError}</div>;
          }
          return (
            <div
              data-testid="landing-categories-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 16,
              }}
            >
              {categories.map((cat) => (
                <div key={cat.id} className="sf-card" data-testid={`landing-category-${cat.id}`}>
                  <div className="sf-eyebrow" style={{ marginBottom: 10 }}>{cat.name}</div>
                  <p style={{ color: "var(--sf-offwhite)", marginBottom: 18, fontSize: 15 }}>
                    {cat.description}
                  </p>
                  <div className="sf-divider" style={{ marginBottom: 14 }} />
                  <div className="sf-mono" style={{ fontSize: 13, color: "var(--sf-muted)" }}>
                    Typical monthly loss when this system is weak
                  </div>
                  <div className="sf-mono sf-gold" style={{ fontSize: 18, marginTop: 4 }} data-testid={`landing-category-damage-${cat.id}`}>
                    ${cat.monthly_damage_min.toLocaleString()}–${cat.monthly_damage_max.toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        <div style={{ marginTop: 36 }}>
          <Link to="/audit/snapshot" className="sf-btn" data-testid="landing-cta-bottom">
            Start the audit · $97
          </Link>
        </div>
      </section>
    </div>
  );
}
